import { useState, useEffect, useContext } from "react";
import placeholder from "../inc/placeholder.jpg";
import { SiteContext } from "../contexts/SiteContext";
import { useAuth } from "../admin/contexts/AuthContext";
import fetchSite from "../utils/fetchSite";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
	Paper,
	Typography,
	makeStyles,
	Grid,
	Fade,
	Chip,
	Divider,
} from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import Loading from "../components/Loading";
import Header from "../components/Header";
import AppBar from "../components/AppBar";
import Breadcrumbs from "../components/Breadcrumbs";
import Warning from "../components/Warning";
import NotFound from "../components/NotFound";
import AdminBar from "../components/AdminBar";
import NotAllowed from "../components/NotAllowed";
import RelatedPeople from "../components/RelatedPeople";
import RelatedEntries from "../components/RelatedEntries";
import Banner from "../components/Banner";

const fullName = (person) => {
	let name = "";
	if (person.firstName) {
		name += person.firstName;
	}
	if (person.middleName) {
		name += " " + person.middleName;
	}
	if (person.lastName) {
		name += " " + person.lastName;
	}
	if (person.suffix) {
		name += " " + person.suffix;
	}
	if (person.title) {
		name += ", " + person.title;
	}
	return name;
};

function Person() {
	const { isAuth } = useAuth();
	const [loading, setLoading] = useState(true);
	const [person, setPerson] = useState();
	const { selectedSite, setSelectedSite } = useContext(SiteContext);
	const { siteSlug, personSlug } = useParams();

	const useStyles = makeStyles((theme) => ({
		root: {
			flex: 1,
		},
		main: {
			margin: "0 auto",
			padding: "1rem",
			paddingTop: 0,
			maxWidth: process.env.REACT_APP_MAX_WIDTH,
		},
		themes: {
			listStyle: "none",
			display: "flex",
			justifyContent: "center",
			flexWrap: "wrap",
			padding: theme.spacing(0.5),
			margin: 0,
		},
		chip: {
			cursor: "pointer",
		},
		paper: {
			padding: "1rem",
		},
		title: {
			color:
				selectedSite && selectedSite.secondaryColor
					? selectedSite.secondaryColor
					: theme.palette.secondary.main,
		},
		featuredImage: {
			width: "100%",
		},
		button: {
			fontWeight: "bold",
			fontSize: ".7rem",
		},
		link: {
			color: theme.palette.primary.main,
			textDecoration: "none",
		},
	}));

	const classes = useStyles();

	// This effect sets the selectedSite based on the :siteSlug (first url parameter)
	useEffect(() => {
		fetchSite(siteSlug, setSelectedSite);
	}, [siteSlug, setSelectedSite]);

	// This effect sets the person and uses the selectedSite._id to make sure it's on the right site
	useEffect(() => {
		async function fetchPerson() {
			let res = await fetch(
				`${process.env.REACT_APP_API_URL}/people?units.site._id=${selectedSite._id}`,
				{ credentials: "include" }
			);
			res = await res.json();
			if (res.success) {
				res.data = res.data.filter((person) => person.slug === personSlug);
				setPerson(res.data[0]);
				setLoading(false);
			} else {
				setLoading(false);
				console.error(res.error);
			}
		}
		if (selectedSite) {
			fetchPerson();
		}
	}, [selectedSite, personSlug]);

	if (loading || !selectedSite) {
		return <Loading />;
	}

	if (!loading && !person) {
		return <NotFound />;
	}

	// If the resource/site isn't published and the user isn't logged in, don't show the page
	if (!isAuth && (!person.published || !selectedSite.published)) {
		return <NotAllowed />;
	}

	return (
		<Fade in={typeof person != "undefined"}>
			<div className={classes.root}>
				<AdminBar />
				<Header />
				<AppBar />
				<Banner />
				<Breadcrumbs
					links={[
						{
							name: "People",
							url: `/${selectedSite.slug}/people`,
						},
						{
							name: fullName(person),
							url: `/${selectedSite.slug}/people/${person.slug}`,
						},
					]}
				/>
				<main className={classes.main}>
					<Grid container spacing={3}>
						<Grid item sm={12} md={4}>
							<Paper style={{ marginBottom: "1rem" }} className={classes.paper}>
								<img
									className={classes.featuredImage}
									src={
										person.featuredImage
											? `${process.env.REACT_APP_FILE_URL}/${person.featuredImage.fileName}`
											: //: "https://source.unsplash.com/random?collection=1995427&size=500x500"
											  placeholder
									}
									alt={`${fullName(person)} featured`}
								/>

								{person.dob && (
									<>
										<p>
											<b>Date of Birth: </b>
											{person.dob}
										</p>
									</>
								)}
								{person.dod && (
									<>
										<p>
											<b>Date of Death: </b>
											{person.dod}
										</p>
									</>
								)}

								<ul className={classes.themes}>
									{person.themes.map((theme, i) => (
										<li key={i}>
											<Chip
												component={Link}
												to={`/${siteSlug}/themes/${theme.slug}`}
												label={theme.name}
												className={classes.chip}
											/>
										</li>
									))}
								</ul>
							</Paper>
							<Warning margin="0 0 1rem 0" />
						</Grid>
						<Grid item sm={12} md={8}>
							<Paper className={classes.paper}>
								<Typography
									className={classes.title}
									style={{ marginTop: "1rem" }}
									gutterBottom
									component="h2"
									variant="h6"
								>
									<PersonIcon /> Person
								</Typography>
								<Typography
									style={{ marginTop: "1rem" }}
									gutterBottom
									component="h2"
									variant="h5"
								>
									{fullName(person)}
								</Typography>
								{person.bio && (
									<>
										<div dangerouslySetInnerHTML={{ __html: person.bio }}></div>
									</>
								)}
								{person.fileCitation && (
									<>
										<Divider variant="middle" />
										<Typography
											style={{ marginTop: "1rem" }}
											gutterBottom
											component="h2"
											variant="h6"
										>
											File Citation
										</Typography>
										<div
											dangerouslySetInnerHTML={{ __html: person.fileCitation }}
										></div>
									</>
								)}
								{person.relatedPeople && person.relatedPeople.length > 0 && (
									<RelatedPeople people={person.relatedPeople} />
								)}
								{person.relatedEntries && person.relatedEntries.length > 0 && (
									<RelatedEntries entries={person.relatedEntries} />
								)}
							</Paper>
						</Grid>
					</Grid>
				</main>
			</div>
		</Fade>
	);
}

export default Person;
